/**
 * Form schema types — định nghĩa cho Visual Builder paper-form.
 *
 * Block types:
 *  - header: logo + tên công ty + form code
 *  - title: tiêu đề lớn căn giữa
 *  - text: đoạn text thường
 *  - section_title: heading section (I, II, III)
 *  - field: 1 input field với label inline
 *  - field_row: nhiều field cùng 1 dòng
 *  - checkbox_row: nhiều checkbox 1 dòng (có thể exclusive)
 *  - table: bảng có thể thêm/xóa dòng (loop {{#items}})
 *  - checklist: danh sách checkbox với option input text kèm theo
 *  - signatures: vùng chữ ký nhiều người
 */

export type InputType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'auto_index';   // chỉ dùng trong table column - hiển thị 1,2,3...

export interface FieldBlock {
  type: 'field';
  name: string;
  label: string;
  input: InputType;
  required?: boolean;
  align?: 'left' | 'center' | 'right';
  /** Tự fill từ user/today. Values: current_user_name, current_user_dept, today */
  autoFill?: 'current_user_name' | 'current_user_dept' | 'today';
  defaultToday?: boolean;
  /** Hiển thị "đồng" sau input */
  suffix?: string;
  /** Indent left (cho sub-fields trong section) */
  indent?: boolean;
  /** Italic text */
  italic?: boolean;
  /** Readonly = computed only, không cho edit */
  readonly?: boolean;
  /** Expression cho computed value. Dùng tên các field khác.
   *  VD: "max(0, tam_ung - tong_cong)"
   *  Hỗ trợ: + - * / ( ) max() min() và tên field
   */
  computed?: string;
  /** Đặc biệt: lấy số từ field khác và đổi sang chữ tiếng Việt */
  computedWords?: string;
  /** Default value khi chưa fill */
  default?: string;
}

export interface FieldRowBlock {
  type: 'field_row';
  fields: Omit<FieldBlock, 'type'>[];
}

export interface CheckboxRowBlock {
  type: 'checkbox_row';
  items: Array<{
    name: string;
    label: string;
    /** Các checkbox cùng exclusiveGroup chỉ được tick 1 (radio-like) */
    exclusiveGroup?: string;
    default?: boolean;
  }>;
}

export interface TableColumn {
  name: string;
  label: string;
  input: InputType;
  width?: number;
  default?: string;
  /** Computed expr dùng tên các column khác cùng row. VD "so_luong * don_gia" */
  computed?: string;
}

export interface TableTotal {
  name: string;
  label: string;
  /** Tên column cần SUM */
  sumColumn: string;
}

export interface TableBlock {
  type: 'table';
  name: string;       // tên field gốc -> sẽ là JSON array khi render
  columns: TableColumn[];
  totals?: TableTotal[];
  addable?: boolean;
  minRows?: number;
}

export interface ChecklistBlock {
  type: 'checklist';
  label: string;
  columns?: number;  // 1 hoặc 2 cột layout
  items: Array<{
    name: string;
    label: string;
    /** Có ô input text bên cạnh checkbox hay không? */
    withInput?: boolean;
    /** Placeholder cho input phụ */
    inputPlaceholder?: string;
  }>;
}

export interface TextBlock {
  type: 'text';
  text: string;
  bold?: boolean;
  italic?: boolean;
  align?: 'left' | 'center' | 'right';
}

export interface SectionTitleBlock {
  type: 'section_title';
  text: string;
}

export interface TitleBlock {
  type: 'title';
  text: string;
}

export interface HeaderBlock {
  type: 'header';
  showLogo?: boolean;
  showCompany?: boolean;
}

export interface SignaturesBlock {
  type: 'signatures';
  items: Array<SignatureSlot>;
  /** 'row' (mặc định) - các cột nằm ngang | 'grid' - dạng table có viền (mẫu #10, #11, #12) */
  layout?: 'row' | 'grid';
}

/**
 * 1 ô chữ ký trong khối "signatures".
 * Phase A: pre-config người ký + role để tạo phiếu không cần kéo thả vị trí + chọn signer.
 */
export interface SignatureSlot {
  /** Label hiển thị trên ô (vd "Trưởng phòng IT") */
  label: string;
  /** Khi true — auto fill tên user đang đăng nhập (giữ tương thích cũ) */
  autoFillUser?: boolean;

  // ─── Phase A — Pre-configured signer ───
  /** Cấu hình signer cho block này (Phase A). Nếu không có → fallback chế độ kéo thả cũ. */
  signerConfig?: {
    /**
     * - FIXED_USER: chọn cứng 1 user
     * - ROLE: resolve động theo vai trò (DEPT_HEAD/BOD/...)
     * - CREATOR: người tạo yêu cầu (user đang đăng nhập, người gen PDF từ template)
     * - NONE: bỏ trống, user chọn lúc tạo phiếu
     */
    mode: 'FIXED_USER' | 'ROLE' | 'CREATOR' | 'NONE';
    /** Khi mode = FIXED_USER */
    fixedUserId?: string;
    fixedUserName?: string;       // cache để hiển thị (nếu user bị xóa, vẫn show được)
    fixedUserPosition?: string;
    /** Khi mode = ROLE */
    role?: 'DEPT_HEAD' | 'BOD' | 'SYS_ADMIN' | string;
    /** Phạm vi resolve role */
    departmentScope?: 'SAME_AS_DOC' | 'SPECIFIC';
    specificDepartmentId?: string;
    /** Thứ tự ký (cho workflow tuần tự). Nhỏ ký trước. */
    order?: number;
    /** Required */
    required?: boolean;
    /** Tùy chọn hiển thị trên PDF khi đã ký */
    showName?: boolean;
    showPosition?: boolean;
    showDate?: boolean;
    showSigCode?: boolean;
  };
}

// =============================================================
// 6 block mới (Phase 5)
// =============================================================

/**
 * Header 2 cột — vd "Sở Y tế Hà Nội / Cộng hòa Xã hội Chủ nghĩa VN" (mẫu BBKT #6)
 * Khác header thường ở chỗ KHÔNG có logo, mỗi bên là 2 dòng text bold.
 */
export interface Header2ColBlock {
  type: 'header_2col';
  left: {
    line1: string;
    line2?: string;
  };
  right: {
    line1: string;
    line2?: string;
  };
}

/**
 * Section Bên A / Bên B — vd "Bên giao hàng" / "Bên nhận hàng" (mẫu giao nhận #8, #9)
 * Có 1 title + danh sách đại diện (có thể thêm/xóa). Tùy chọn có địa chỉ.
 */
export interface PartySectionBlock {
  type: 'party_section';
  title: string;
  /** Tên field gốc — backend sẽ render thành array of representatives */
  name: string;
  /** Có hiện row "Địa chỉ:" trên đầu không */
  showAddress?: boolean;
  /** Số đại diện mặc định */
  defaultReps?: number;
  /** Tối đa đại diện */
  maxReps?: number;
}

/**
 * Bank info group — vd "Người thụ hưởng / Số TK / Ngân hàng" (mẫu DNTU #3, TUCN #4)
 * Group 3 field bank info, có conditional show theo "Hình thức thanh toán" = "Chuyển khoản"
 */
export interface BankInfoBlock {
  type: 'bank_info';
  /** Tên field "Hình thức thanh toán" - nếu có, chỉ hiện khi field này = 'Chuyển khoản' */
  showWhenField?: string;
  showWhenValue?: string;
  beneficiaryName?: string;  // tên field, mặc định 'beneficiary'
  accountNumberName?: string; // mặc định 'account_number'
  bankName?: string;         // mặc định 'bank_name'
}

/**
 * Bảng có nhóm cột (multi-level header) — vd "THAY ĐỔI TỪ" / "THAY ĐỔI THÀNH" (mẫu #12)
 * Cột được nhóm thành các group, mỗi group có header riêng phía trên.
 */
export interface TableGroupedBlock {
  type: 'table_grouped';
  name: string;
  /** Các nhóm cột — render header cấp 1 với colspan */
  groups: Array<{
    label: string;
    /** Số cột thuộc group này */
    colspan: number;
    /** Style header cho group */
    background?: string;
  }>;
  /** Cột chi tiết — phải khớp tổng colspan của groups */
  columns: TableColumn[];
  totals?: TableTotal[];
  addable?: boolean;
  minRows?: number;
}

/**
 * Header info 2 cột (text bold + nội dung) — vd "Bộ phận yêu cầu / Bộ phận tiếp nhận" (mẫu #11, #12)
 * Khác field_row ở chỗ là grid bordered (giống bảng) với 2 ô.
 */
export interface InfoGridBlock {
  type: 'info_grid';
  /** Số cột grid (2-4) */
  columns: number;
  cells: Array<{
    label: string;
    name: string;
    input?: 'text' | 'date';
    autoFill?: 'current_user_name' | 'current_user_dept' | 'today';
  }>;
}

/**
 * Spacer / Divider — đường kẻ ngang hoặc khoảng trống
 */
export interface SpacerBlock {
  type: 'spacer';
  /** Loại: 'line' = đường kẻ, 'space' = khoảng trống */
  kind?: 'line' | 'space';
  /** Chiều cao (px) */
  height?: number;
}

export type FormBlock =
  | HeaderBlock
  | Header2ColBlock
  | TitleBlock
  | TextBlock
  | SectionTitleBlock
  | FieldBlock
  | FieldRowBlock
  | CheckboxRowBlock
  | TableBlock
  | TableGroupedBlock
  | ChecklistBlock
  | PartySectionBlock
  | BankInfoBlock
  | InfoGridBlock
  | SpacerBlock
  | SignaturesBlock;

export interface FormSchema {
  version: number;
  paperSize?: 'A4' | 'A5';
  formCode?: string;
  blocks: FormBlock[];
}

/**
 * Parse form schema JSON với fallback an toàn.
 */
export function parseFormSchema(json: string | null | undefined): FormSchema | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (typeof parsed !== 'object' || !Array.isArray(parsed.blocks)) return null;
    return parsed as FormSchema;
  } catch {
    return null;
  }
}

/**
 * Eval computed expression trong context của data.
 * Hỗ trợ: + - * / ( ) max(...) min(...) và tên field/column.
 * Bảo mật: không có Function() arbitrary - chỉ parse các operator cho phép.
 *
 * Vì user không nhập trực tiếp expression (admin chỉ paste JSON),
 * em dùng Function() đơn giản nhưng sanitize trước.
 */
export function evalComputed(expr: string, ctx: Record<string, number>): number {
  // Sanitize: chỉ cho ký tự an toàn
  const safe = expr.replace(/[^a-zA-Z0-9_+\-*/().,\s]/g, '');
  if (safe !== expr.replace(/\s+/g, ' ').trim()) {
    // Có ký tự nguy hiểm -> reject
    return 0;
  }

  // Replace tên biến bằng ctx[name]
  const varNames = Array.from(new Set(safe.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || []));
  const builtins = new Set(['max', 'min', 'abs', 'round', 'floor', 'ceil']);
  const args: string[] = [];
  const argVals: number[] = [];
  let body = safe;
  for (const v of varNames) {
    if (builtins.has(v)) continue;
    args.push(v);
    argVals.push(ctx[v] ?? 0);
  }
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('max', 'min', 'abs', 'round', 'floor', 'ceil', ...args, `return (${body});`);
    const result = fn(Math.max, Math.min, Math.abs, Math.round, Math.floor, Math.ceil, ...argVals);
    return typeof result === 'number' && isFinite(result) ? result : 0;
  } catch {
    return 0;
  }
}
